import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
import namesPlugin from 'colord/plugins/names';

// `names` plugin teaches colord to parse CSS keyword colors ('white',
// 'black', 'red', etc.). Without it, the palette's `'white'` / `'black'`
// swatches normalize to null → contrast check short-circuits to "pass" →
// they stay enabled even when contrast is genuinely poor.
extend([a11yPlugin, namesPlugin]);

/**
 * Tiny WCAG helper used by ColorsInput / CommonInputs to gate palette swatches
 * when `window.galaxyWcag === true`. All pure functions — safe to call inside
 * computed properties. Transparent / empty / invalid colors short-circuit to
 * "pass" so we never lock editors out of picking a known-good neutral while
 * the sibling field is still unset.
 */

const NON_CHROMATIC = new Set(['transparent', '', null, undefined]);

// WCAG 2.1 AA ratios:
//   4.5:1 for normal body text
//   3.0:1 for large text (≥18pt or ≥14pt bold) — also the bar for UI bg/fg pairs
//   7.0:1 for AAA body text
const RATIOS = {
    AA: 4.5,
    AA_LARGE: 3.0,
    AAA: 7.0,
};

function normalize(value) {
    if (NON_CHROMATIC.has(value)) return null;
    const parsed = colord(value);
    return parsed.isValid() ? parsed : null;
}

/**
 * Returns the contrast ratio between two colors, or null if either value is
 * non-chromatic (transparent / unset / invalid). `null` means "unknown" →
 * callers should treat that as a pass so the picker doesn't lock up before
 * both fields have a real color.
 */
export function contrastRatio(a, b) {
    const colorA = normalize(a);
    const colorB = normalize(b);
    if (!colorA || !colorB) return null;
    return colorA.contrast(colorB);
}

/**
 * True if `candidate` meets AA contrast against `against`. Defaults to the
 * 4.5:1 body-text ratio. Set `level: 'AA_LARGE'` for UI / ≥18pt text (3:1),
 * or `'AAA'` for the stricter 7:1 body bar.
 */
export function meetsContrast(candidate, against, level = 'AA') {
    const ratio = contrastRatio(candidate, against);
    if (ratio === null) return true; // unknown → don't disable
    return ratio >= (RATIOS[level] ?? RATIOS.AA);
}
